import { CAMERA_FACING, CameraListener } from 'mind-sdk-web';
import { Camera as MindCamera } from 'mind-sdk-web/lib/Camera';
import { MediaStreamVideoConsumer } from 'mind-sdk-web/lib/MediaStream';
import { DeviceRegistry } from '../../../types';

type CameraProps = {
  deviceRegistry: DeviceRegistry;
  mindCamera: MindCamera;
  decoratedLabel?: string;
  description?: string;
};

export class Camera implements CameraListener {
  private deviceRegistry: DeviceRegistry;
  private mindCamera: MindCamera;

  private listener: CameraListener | null = null;

  private decoratedLabel: string;
  private description: string;

  private acquired: boolean = false;
  private acquiring: boolean = false;

  private static readonly MAX_RETRY = 3;
  private static readonly RETRY_DELAY_MS = 500;
  private retryCount: number = 0;

  constructor(props: CameraProps) {
    this.deviceRegistry = props.deviceRegistry;
    this.mindCamera = props.mindCamera;
    this.decoratedLabel = props.decoratedLabel ?? '';
    this.description = props.description ?? '';

    this.mindCamera.setListener(this);
  }

  isDefault(): boolean {
    const defaultCamera = this.deviceRegistry
      .getDevicesInfo()
      .find((device) => device.kind === 'videoinput' && device.deviceId === 'default');
    return defaultCamera?.deviceId === this.getId() || this.getId() === 'default';
  }

  getDescription(): string {
    return this.description;
  }

  isAcquired(): boolean {
    return this.acquired;
  }

  isAcquiring(): boolean {
    return this.acquiring;
  }

  getId(): string {
    return this.mindCamera.getId();
  }
  getLabel(): string {
    return this.decoratedLabel ?? this.mindCamera.getLabel();
  }

  async acquire(): Promise<void> {
    console.log(`Acquiring camera '${this.mindCamera.getLabel()}'`);

    if (this.acquiring) {
      throw new Error('Camera is already acquiring');
    }

    this.acquiring = true;
    try {
      await this.mindCamera.acquire();
      this.acquired = true;
      this.retryCount = 0;
    } finally {
      this.acquiring = false;
      console.log(`Camera '${this.mindCamera.getLabel()}' acquired: ${this.acquired}`);
    }
  }

  release(): void {
    console.log(`Releasing camera '${this.mindCamera.getLabel()}'`);

    if (this.acquiring) console.warn(`Camera '${this.mindCamera.getLabel()}' is releasing during acquire`);

    if (!this.acquired) return;

    this.mindCamera.release();
    this.acquired = false;
  }

  setFacing(facing: CAMERA_FACING): void {
    if (this.acquiring) console.warn('Camera is acquiring, facing change may not take effect');
    this.mindCamera.setFacing(facing);
  }

  setResolution(width: number, height: number): void {
    if (this.acquiring) console.warn('Camera is acquiring, resolution change may not take effect');
    this.mindCamera.setResolution(width, height);
  }

  setFps(fps: number): void {
    if (this.acquiring) console.warn('Camera is acquiring, fps change may not take effect');
    this.mindCamera.setFps(fps);
  }

  setBitrate(bitrate: number): void {
    if (this.acquiring) console.warn('Camera is acquiring, bitrate change may not take effect');
    this.mindCamera.setBitrate(bitrate);
  }

  setAdaptivity(adaptivity: number): void {
    if (this.acquiring) console.warn('Camera is acquiring, adaptivity change may not take effect');
    this.mindCamera.setAdaptivity(adaptivity);
  }

  addVideoConsumer(videoConsumer: MediaStreamVideoConsumer): void {
    this.mindCamera.addVideoConsumer(videoConsumer);
  }

  removeVideoConsumer(videoConsumer: MediaStreamVideoConsumer): void {
    this.mindCamera.removeVideoConsumer(videoConsumer);
  }

  destroy(): void {
    console.log(`Destroying camera '${this.mindCamera.getLabel()}'`);

    if (this.acquiring) console.warn(`Camera '${this.mindCamera.getLabel()}' is acquiring during destroy`);

    this.mindCamera.destroy();
  }

  fireVideoBuffer(): void {
    console.log(`Firing video buffer for camera '${this.mindCamera.getLabel()}'`);

    this.mindCamera.fireVideoBuffer();
  }

  setListener(cameraListener: CameraListener | null): void {
    this.listener = cameraListener;
  }

  onCameraReleasedForcibly(): void {
    if (this.acquiring) {
      console.warn('Camera released forcibly while acquiring');
      if (this.retryCount < Camera.MAX_RETRY) {
        console.warn('Retrying to acquire camera...');
        this.retryCount++;
        setTimeout(() => {
          this.acquire().catch((e) => {
            console.warn('Retry acquire failed:', e);
          });
        }, Camera.RETRY_DELAY_MS);
      } else {
        console.warn('Max camera acquire retries reached.');
        this.retryCount = 0;
      }
    }

    this.acquired = false;

    if (this.listener) {
      this.listener.onCameraReleasedForcibly(this);
    }
  }
}
